import React, {useRef, useCallback, useState, useEffect} from 'react';
import {Icon} from '@albert-io/atomic';

import './custom-scrollbar.scss';

interface CustomScrollbarProps {
  children: React.ReactNode;
  className?: string;
}

const CustomScrollbar = ({children, className}: CustomScrollbarProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumbHeight, setThumbHeight] = useState(50);
  const [thumbTop, setThumbTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartScrollTop, setDragStartScrollTop] = useState(0);

  const updateThumb = useCallback(() => {
    if (contentRef.current && trackRef.current) {
      const {scrollTop, scrollHeight, clientHeight} = contentRef.current;
      const trackHeight = trackRef.current.clientHeight;

      // Calculate thumb height as a percentage of visible content
      const visibleRatio = clientHeight / scrollHeight;
      const newThumbHeight = Math.max(visibleRatio * trackHeight, 40);
      setThumbHeight(newThumbHeight);

      // Calculate thumb position
      const maxScroll = scrollHeight - clientHeight;
      const scrollRatio = maxScroll > 0 ? scrollTop / maxScroll : 0;
      const maxThumbTop = trackHeight - newThumbHeight;
      setThumbTop(scrollRatio * maxThumbTop);
    }
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', updateThumb);
      // Initial calculation
      updateThumb();

      // Recalculate on resize
      const resizeObserver = new ResizeObserver(updateThumb);
      resizeObserver.observe(content);

      return () => {
        content.removeEventListener('scroll', updateThumb);
        resizeObserver.disconnect();
      };
    }
  }, [updateThumb]);

  const scrollUp = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollBy({top: -100, behavior: 'smooth'});
    }
  }, []);

  const scrollDown = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollBy({top: 100, behavior: 'smooth'});
    }
  }, []);

  const handleThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragStartScrollTop(contentRef.current?.scrollTop || 0);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !contentRef.current || !trackRef.current) return;

      const deltaY = e.clientY - dragStartY;
      const trackHeight = trackRef.current.clientHeight;
      const {scrollHeight, clientHeight} = contentRef.current;
      const maxScroll = scrollHeight - clientHeight;
      const scrollRatio = deltaY / (trackHeight - thumbHeight);

      contentRef.current.scrollTop = dragStartScrollTop + scrollRatio * maxScroll;
    },
    [isDragging, dragStartY, dragStartScrollTop, thumbHeight]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    if (!contentRef.current || !trackRef.current || e.target === thumbRef.current) return;

    const trackRect = trackRef.current.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const trackHeight = trackRef.current.clientHeight;
    const {scrollHeight, clientHeight} = contentRef.current;
    const maxScroll = scrollHeight - clientHeight;

    const scrollRatio = clickY / trackHeight;
    contentRef.current.scrollTo({
      top: scrollRatio * maxScroll,
      behavior: 'smooth'
    });
  }, []);

  return (
    <div className={`custom-scrollbar ${className || ''}`}>
      <div className='custom-scrollbar__content' ref={contentRef}>
        {children}
      </div>
      <div className='custom-scrollbar__track-wrapper'>
        <button
          className='custom-scrollbar__button custom-scrollbar__button--up'
          onClick={scrollUp}
          aria-label='Scroll up'
        >
          <Icon icon='chevron-up' color='primary-inverse-static' />
        </button>
        <div
          className='custom-scrollbar__track'
          ref={trackRef}
          onClick={handleTrackClick}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
              e.preventDefault();
              scrollUp();
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
              e.preventDefault();
              scrollDown();
            } else if (e.key === 'PageUp') {
              e.preventDefault();
              contentRef.current?.scrollBy({top: -300, behavior: 'smooth'});
            } else if (e.key === 'PageDown') {
              e.preventDefault();
              contentRef.current?.scrollBy({top: 300, behavior: 'smooth'});
            } else if (e.key === 'Home') {
              e.preventDefault();
              contentRef.current?.scrollTo({top: 0, behavior: 'smooth'});
            } else if (e.key === 'End') {
              e.preventDefault();
              contentRef.current?.scrollTo({
                top: contentRef.current.scrollHeight,
                behavior: 'smooth'
              });
            }
          }}
          role='scrollbar'
          aria-controls='scrollable-content'
          aria-valuenow={thumbTop}
          tabIndex={-1}
        >
          <div
            className='custom-scrollbar__thumb'
            ref={thumbRef}
            style={{
              height: `${thumbHeight}px`,
              transform: `translateY(${thumbTop}px)`
            }}
            onMouseDown={handleThumbMouseDown}
          />
        </div>
        <button
          type='button'
          className='custom-scrollbar__button custom-scrollbar__button--down'
          onClick={scrollDown}
          aria-label='Scroll down'
        >
          <Icon icon='chevron-down' color='primary-inverse-static' />
        </button>
      </div>
    </div>
  );
};

export default CustomScrollbar;
